import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import Et3Button from "../../core/et3-button";
import { isPositiveNumber, isDefinedString, reduce } from "et3-type-guards";
import getUserDeviceStore from "../../stores/user-store/device-store";
import { colors } from "../../core/et3-theme/colors";
import {
  EditProfileDataAction,
  EditProfileData,
  EditProfileHistory,
  ExtraDataEvents,
  HeaderWithTypeField,
  LinkComponent,
  ReasonsArrayDisplay,
  MessageIDText,
  HeaderWithSubtitle,
  OldExtraDataEvents,
  ClientReadableText,
  MessageHiddenStatus
} from "../../core/et3-table/UserCustomHelpers";
import ET3Div from "../../core/et3-div";
import MessageWithReactionsCell from "../../components/message-reactions/MessageWithReactionsCell";
import getUserMessagesStore from "../../stores/user-store/messages-store";
import getCorePersonalStore from "../../stores/core/core-personal-store";
import i18n from "../i18n";
import Et3Image from "../../core/et3-Image";

export const USER_ADMIN_COMMENTS_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("ID"),
    field: "uniqueId"
  },
  {
    headerName: i18n.get("ADMIN_NAME"),
    field: "adminName"
  },
  {
    headerName: i18n.get("COMMENT"),
    field: "adminComment"
  },
  {
    headerName: i18n.get("DATE_COLUMN"),
    field: "commentDate"
  }
];

export const USER_ADVANCED_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("ROW_NUMBER"),
    field: "uniqueId"
  },
  {
    headerName: i18n.get("TYPE"),
    field: "attribut"
  },
  {
    headerName: i18n.get("VALUE_COLUMN"),
    field: "value"
  }
];

export const USER_ADVANCED_EMAILS_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("ROW_NUMBER"),
    field: "uniqueId",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("EMAIL"),
    field: "userEmail",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("PLATFORM"),
    field: "verificationPlatform",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("VERIFICATION_ACTIVE"),
    field: "verificationActive",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("USER_ACTIVITY_EMAILS_SUBSCRIPTION"),
    field: "userActivityEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("PROMOTIONAL_EMAILS_SUBSCRIPTION"),
    field: "promotionalEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("NEW_MESSAGE_EMAILS_SUBSCRIPTION"),
    field: "newMessageEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("VISIT_PROFILE_EMAILS_SUBSCRIPTION"),
    field: "visitProfileEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("LIKE_EMAILS_SUBSCRIPTION"),
    field: "likeEmailsSubscription",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("USER_ACTIVITY_EMAILS_SETTINGS"),
    field: "userActivityEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("PROMOTIONAL_EMAILS_SETTINGS"),
    field: "promotionalEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("NEW_MESSAGE_EMAILS_SETTINGS"),
    field: "newMessageEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("VISIT_PROFILE_EMAILS_SETTINGS"),
    field: "visitProfileEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  },
  {
    headerName: i18n.get("LIKE_EMAILS_SETTINGS"),
    field: "likeEmailsSettingsOn",
    headerClassName: "headerUserAdvanced"
  }
];

export const USER_ADVANCED_QUIZ_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("QUESTION_ID"),
    field: "questionId"
  },
  {
    headerName: i18n.get("ALLOWED_ANSWERS"),
    field: "allowedAnswers"
  },
  {
    headerName: i18n.get("QUESTION"),
    field: "questionTextEnglish",
    flex: 2.5
  },
  {
    headerName: i18n.get("ANSWER"),
    field: "answerTextEnglish",
    flex: 2.5
  }
];

export const USER_DEVICES_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "User ID",
    field: "userId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userId}`}
          text={`${params.row.userId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Device ID",
    field: "deviceId"
  },
  {
    headerName: "Is Active",
    field: "isActiveStatus",
    renderCell: (params: GridValueGetterParams) => {
      const isActive = params.row.isActive;
      const isActiveUserUUID = params.row.userUUID;
      const deviceId = params.row.deviceId;
      return isPositiveNumber(isActive) ? (
        <Et3Button
          isEnabled={true}
          onClick={() => {
            getUserDeviceStore().unlinkDevice({
              userUUID: reduce(() => isActiveUserUUID, ""),
              deviceId
            });
          }}
          size="LARGE"
          text="UNLINK_DEVICE"
          type="WARN"
        />
      ) : (
        <></>
      );
    }
  }
];

export const USER_CONVERSATIONS_HEADERS: GridColDef[] = [
  {
    headerName: "Message ID",
    field: "messageId",
    renderCell: (params: GridValueGetterParams) => {
      return <MessageIDText messageID={`${params.row.messageId}`} />;
    }
  },
  {
    headerName: "Sender",
    field: "senderId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.senderId}`}
          text={`${params.row.senderId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Recipient",
    field: "recipientId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.recipientId}`}
          text={`${params.row.recipientId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Message",
    field: "message",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return <MessageWithReactionsCell row={params.row} />;
    }
  },
  {
    headerName: "Hidden",
    field: "isDeleted",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <MessageHiddenStatus
          isDeleted={params.row.isDeleted}
          deletedOn={params.row.deletedOn}
        />
      );
    }
  },
  {
    headerName: "Sent On",
    field: "sentOn"
  },
  {
    headerName: "Message Type",
    field: "messageType",
    flex: 1.5
  }
];

export const USER_EDIT_PROFILE_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "key",
    field: "key"
  },
  {
    headerName: "value",
    field: "editProfileValue",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <EditProfileData
          editedValue={params.row.value}
          editProfileKey={params.row.key}
        />
      );
    }
  },
  {
    headerName: "# of changed",
    field: "numberOfChange"
  },
  {
    headerName: "Action",
    field: "editProfileAction",
    renderCell: (params: GridValueGetterParams) => {
      return <EditProfileDataAction actionKey={`${params.row.key}`} />;
    }
  }
];

export const USER_EDIT_PROFILE_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },

  {
    headerName: "description",
    field: "description",
    renderCell: (params: GridValueGetterParams) => {
      return <EditProfileHistory description={params.row.description} />;
    }
  },
  {
    headerName: "Changed at",
    field: "dateChange"
  },
  {
    headerName: "Changed by",
    field: "changedBy"
  }
];

export const USER_EVENTS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId",
    flex: 0.3
  },
  {
    headerName: "Admin Name",
    field: "adminName"
  },
  {
    headerName: "Type",
    field: "type",
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Old Extra Data",
    field: "oldExtraData",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <OldExtraDataEvents
          eventType={params.row.type}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Extra Data",
    field: "extraData",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ExtraDataEvents
          eventType={params.row.type}
          imageSource={params.row.image_source}
          description={params.row.description}
          reportID={params.row.reportID}
          temporaryBanNumberOfDays={params.row.temporaryBanNumberOfDays}
          temporaryBanUntil={params.row.temporaryBanUntil}
          investigationCaseID={params.row.investigationCaseID}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Page Name",
    field: "pageName",
    renderCell: (params: GridValueGetterParams) => {
      const { pageName, userID, reportID } = params.row;
      let hrefVal = `/users/advanced?id=${userID}`;
      const isAnyKhattabaAdmin =
        getCorePersonalStore().isAnyKhattabaAdmin.get();

      if (isPositiveNumber(userID)) {
        if (pageName === "USER_ADVANCED") {
          hrefVal = `/users/advanced?id=${userID}`;
        }
        if (pageName === "USER_CONVERSATIONS") {
          hrefVal = `/users/messages?id=${userID}`;
        }
        if (pageName === "REPORTS") {
          hrefVal = `/reports?id=${reportID}`;
        }
        if (pageName === "SCSR_GIVE_SUBSCRIPTIONS") {
          hrefVal = `/scsr/giveSubscriptions`;
        }
        if (pageName === "DEVELOPER_TRANSFER_USER_PACKAGE") {
          hrefVal = `/developer/transferUserPackage`;
        }
      }
      return isAnyKhattabaAdmin ? (
        <span>{pageName}</span>
      ) : isDefinedString(pageName) ? (
        <LinkComponent href={`${hrefVal}`} text={`${pageName}`} style={{}} />
      ) : (
        <LinkComponent href={`${hrefVal}`} text={`USER ADVANCED`} style={{}} />
      );
    }
  },
  {
    headerName: "TBW Reasons",
    field: "tbw_reasons",
    renderCell: (params: GridValueGetterParams) => {
      return <ReasonsArrayDisplay reasons={params.row.tbw_reasons} />;
    }
  },
  {
    headerName: "Date",
    field: "date"
  }
];

export const USER_EVENTS_VERIFY_EMAIL_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Date",
    field: "date"
  },
  {
    headerName: "Email Used",
    field: "email"
  },
  {
    headerName: "Verified",
    field: "verified"
  }
];

export const USER_DEACTIVATIONS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Type",
    field: "type"
  },
  {
    headerName: "Date",
    field: "date"
  }
];

export const USER_LOG_IN_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },

  {
    headerName: "Operating System",
    field: "operatingSystem"
  },
  {
    headerName: "Date",
    field: "createdOn"
  }
];

export const USER_MESSAGES_HEADERS: GridColDef[] = [
  {
    headerName: "Message ID",
    field: "messageId"
  },
  {
    headerName: "Sender",
    field: "senderId",
    renderCell: (params: GridValueGetterParams) => {
      const hrefVal = `/users/advanced?id=${params.row.senderId}`;
      return (
        <LinkComponent
          href={`${hrefVal}`}
          text={params.row.senderId}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Recipient",
    field: "recipientId",
    renderCell: (params: GridValueGetterParams) => {
      const { senderId, recipientId } = params.row;
      const href = `/users/conversations?senderID=${senderId}&&recipientID=${recipientId}`;
      return (
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          fDirection="row"
          justifyContent="center"
          alignItems="center"
          withShadow={false}
          width={180}
          height={50}
          color={colors.all.transparent}
          style={{}}
          onClick={() => {
            getUserMessagesStore().setRecipientColored(recipientId, true);
          }}
        >
          <LinkComponent href={`${href}`} text={recipientId} style={{}} />
        </ET3Div>
      );
    }
  },
  {
    headerName: "Message",
    field: "message",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return <MessageWithReactionsCell row={params.row} />;
    }
  },
  {
    headerName: "Hidden",
    field: "isDeleted",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <MessageHiddenStatus
          isDeleted={params.row.isDeleted}
          deletedOn={params.row.deletedOn}
        />
      );
    }
  },
  {
    headerName: "Sent On",
    field: "sentOn"
  },
  {
    headerName: "Message Type",
    field: "messageType"
  }
];

export const USER_PAYMENTS_PAID_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "userID",
    field: "userID"
  },
  {
    headerName: "Transaction ID",
    field: "transactionID",
    flex: 2
  },
  {
    headerName: "Invoice Number",
    field: "invoiceID"
  },
  {
    headerName: "platform",
    field: "platform"
  },
  {
    headerName: "Country",
    field: "countryCode"
  },
  {
    headerName: "Bundle",
    field: "bundle",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.bundle} />;
    }
  },
  {
    headerName: "Type",
    field: "eventType",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.eventType} />;
    }
  },
  {
    headerName: "Expiry",
    field: "expiryDate"
  },
  {
    headerName: "Event Date",
    field: "createdOn"
  }
];

export const USER_PROMO_CODE_USAGE_HEADRS: GridColDef[] = [
  {
    headerName: "#",
    field: "uniqueId",
    flex: 0.5
  },
  {
    headerName: "Promo Code",
    field: "promocode"
  },
  {
    headerName: "Get Reward",
    field: "isUserRewarded",
    flex: 0.5
  },
  {
    headerName: "Reward Type",
    field: "rewardType",
    flex: 0.5
  },

  {
    headerName: "Reward Amount In Hours",
    field: "rewardAmountInHours",
    flex: 0.5
  },
  {
    headerName: "Source",
    field: "source",
    flex: 0.5
  },
  {
    headerName: "Reason",
    field: "reason"
  },
  {
    headerName: "Created On",
    field: "createdOn"
  }
];

export const USER_REFERRAL_CODE_USAGE_HEADERS: GridColDef[] = [
  {
    headerName: "#",
    field: "uniqueId"
  },
  {
    headerName: "Date Used",
    field: "createdOn",
    flex: 2
  },
  {
    headerName: "Owner",
    field: "peerId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.peerId}`}
          text={`${params.row.peerId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Owner Platform",
    field: "ownerPlatform"
  },
  {
    headerName: "Code",
    field: "referralCode"
  },
  {
    headerName: "Owner Reward",
    field: "ownerReward"
  },
  {
    headerName: "User Reward",
    field: "userReward"
  },
  {
    headerName: "Validity",
    field: "codeValidityOrReason",
    flex: 2
  },
  {
    headerName: "User",
    field: "userId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userId}`}
          text={`${params.row.userId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "User Platform",
    field: "userPlatform"
  }
];

export const SUBSCRIPTION_PAYMENT_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userID"
  },
  {
    headerName: "Transaction ID",
    field: "transactionID",
    flex: 2.5
  },
  {
    headerName: "Invoice Number",
    field: "invoiceID",
    flex: 1.5
  },
  {
    headerName: "Platform",
    field: "platform"
  },
  {
    headerName: "Country",
    field: "country"
  },
  {
    headerName: "Payment Method",
    field: "paymentMethod"
  },
  {
    headerName: "Payment Source",
    field: "paymentSource"
  },
  {
    headerName: "Subscription Status",
    field: "subscriptionStatus",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.subscriptionStatus} />;
    }
  },
  {
    headerName: "Event Date",
    field: "createdOn"
  },
  {
    headerName: "Expiry Date",
    field: "expiryDate"
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.5,
    renderHeader: () => {
      return (
        <HeaderWithSubtitle
          title={i18n.get("AMOUNT")}
          subtitle={i18n.get("FREE_PAID_IN_HOURS_DAYS")}
        />
      );
    }
  },
  {
    headerName: "Description",
    field: "description",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.description} />;
    }
  }
];

export const SUBSCRIPTION_REWIND_BOOST_PAYMENT_HISTORY_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userID"
  },
  {
    headerName: "Transaction ID",
    field: "transactionID",
    flex: 2.5
  },
  {
    headerName: "Invoice Number",
    field: "invoiceID",
    flex: 1.5
  },
  {
    headerName: "Platform",
    field: "platform"
  },
  {
    headerName: "Country",
    field: "country"
  },
  {
    headerName: "Payment Method",
    field: "paymentMethod"
  },
  {
    headerName: "Payment Source",
    field: "paymentSource"
  },
  {
    headerName: "Subscription Status",
    field: "subscriptionStatus",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.subscriptionStatus} />;
    }
  },
  {
    headerName: "Event Date",
    field: "createdOn"
  },
  {
    headerName: "Expiry Date",
    field: "expiryDate"
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.5,
    renderHeader: () => {
      return (
        <HeaderWithSubtitle
          title={i18n.get("AMOUNT")}
          subtitle={i18n.get("COUNT_HINT_SUBTEXT")}
        />
      );
    }
  },
  {
    headerName: "Description",
    field: "description",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.description} />;
    }
  }
];

export const USER_SELFIE_VERIFICATION_PHOTOS_HEADERS: GridColDef[] = [
  {
    headerName: "ID",
    field: "id"
  },
  {
    headerName: "user_id",
    field: "user_id"
  },
  {
    headerName: "matchResult",
    field: "matchResult"
  },
  {
    headerName: "similarity",
    field: "similarity"
  },
  {
    headerName: "selfiePicURL",
    field: "selfiePicURL",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <Et3Image
          type={"ONLINE"}
          alt={i18n.get("ALKHATTABA_ADMIN")}
          imageName={`${params.row.selfiePicURL}`}
          size={140}
          onClick={() => {}}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "pictureToBeComparedURL",
    field: "pictureToBeComparedURL",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <Et3Image
          type={"ONLINE"}
          alt={i18n.get("ALKHATTABA_ADMIN")}
          imageName={`${params.row.pictureToBeComparedURL}`}
          size={140}
          onClick={() => {}}
          style={{}}
        />
      );
    }
  }
];

export const USER_AI_DESCRIPTIONS_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("ID"),
    field: "id"
  },
  {
    headerName: i18n.get("USER_ID"),
    field: "userId"
  },
  {
    headerName: i18n.get("GPT_MODEL"),
    field: "gptModel",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={isDefinedString(params.row.gptModel) ? params.row.gptModel : ""}
        />
      );
    }
  },
  {
    headerName: i18n.get("CLAUDE_MODEL"),
    field: "claudeModel",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.claudeModel)
              ? params.row.claudeModel
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("GPT_DESCRIPTION"),
    field: "gptDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.gptDescription)
              ? params.row.gptDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("CLAUDE_DESCRIPTION"),
    field: "claudeDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.claudeDescription)
              ? params.row.claudeDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("FINAL_DESCRIPTION"),
    field: "finalDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.finalDescription)
              ? params.row.finalDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("MATCH_PERCENTAGE_GPT"),
    field: "gptMatchPercentage",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      const bothExist =
        isDefinedString(params.row.gptDescription) &&
        `${params.row.gptDescription}` !== "" &&
        isDefinedString(params.row.finalDescription) &&
        `${params.row.finalDescription}` !== "";
      return (
        <ClientReadableText
          text={bothExist ? `${params.row.gptMatchPercentage}%` : ""}
        />
      );
    }
  },
  {
    headerName: i18n.get("MATCH_PERCENTAGE_CLAUDE"),
    field: "claudeMatchPercentage",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      const bothExist =
        isDefinedString(params.row.claudeDescription) &&
        `${params.row.claudeDescription}` !== "" &&
        isDefinedString(params.row.finalDescription) &&
        `${params.row.finalDescription}` !== "";
      return (
        <ClientReadableText
          text={bothExist ? `${params.row.claudeMatchPercentage}%` : ""}
        />
      );
    }
  }
];

export const ALL_USERS_AI_DESCRIPTIONS_HEADERS: GridColDef[] = [
  {
    headerName: i18n.get("ID"),
    field: "id"
  },
  {
    headerName: i18n.get("USER_ID"),
    field: "userId"
  },
  {
    headerName: i18n.get("USAGE"),
    field: "usage",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={isDefinedString(params.row.usage) ? params.row.usage : ""}
        />
      );
    }
  },
  {
    headerName: i18n.get("GPT_MODEL"),
    field: "gptModel",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={isDefinedString(params.row.gptModel) ? params.row.gptModel : ""}
        />
      );
    }
  },
  {
    headerName: i18n.get("CLAUDE_MODEL"),
    field: "claudeModel",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.claudeModel)
              ? params.row.claudeModel
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("GPT_DESCRIPTION"),
    field: "gptDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.gptDescription)
              ? params.row.gptDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("CLAUDE_DESCRIPTION"),
    field: "claudeDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.claudeDescription)
              ? params.row.claudeDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("FINAL_DESCRIPTION"),
    field: "finalDescription",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ClientReadableText
          text={
            isDefinedString(params.row.finalDescription)
              ? params.row.finalDescription
              : ""
          }
        />
      );
    }
  },
  {
    headerName: i18n.get("MATCH_PERCENTAGE_GPT"),
    field: "gptMatchPercentage",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      const bothExist =
        isDefinedString(params.row.gptDescription) &&
        `${params.row.gptDescription}` !== "" &&
        isDefinedString(params.row.finalDescription) &&
        `${params.row.finalDescription}` !== "";
      return (
        <ClientReadableText
          text={bothExist ? `${params.row.gptMatchPercentage}%` : ""}
        />
      );
    }
  },
  {
    headerName: i18n.get("MATCH_PERCENTAGE_CLAUDE"),
    field: "claudeMatchPercentage",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      const bothExist =
        isDefinedString(params.row.claudeDescription) &&
        `${params.row.claudeDescription}` !== "" &&
        isDefinedString(params.row.finalDescription) &&
        `${params.row.finalDescription}` !== "";
      return (
        <ClientReadableText
          text={bothExist ? `${params.row.claudeMatchPercentage}%` : ""}
        />
      );
    }
  }
];

export const USER_CONSUMABLES_HEADERS: GridColDef[] = [
  {
    headerName: "NO #",
    field: "uniqueId"
  },
  {
    headerName: "Type",
    field: "attribut"
  },
  {
    headerName: "Value",
    field: "value"
  }
];

export const SILENTLY_BANNED_USERS_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userId}`}
          text={`${params.row.userId}`}
          style={{}}
        />
      );
    },
    width: 120
  },
  {
    headerName: "Username",
    field: "username",
    width: 200
  },
  {
    headerName: "Status",
    field: "status",
    width: 150
  },
  {
    headerName: "Silent Banned On",
    field: "statusChangeDate",
    width: 220
  },
  {
    headerName: "Created On",
    field: "createdOn",
    width: 220
  }
];
